/**
 * 💰 Herrac Pilləli Depozit Servisi
 * 
 * Backend ilə tam sinxronlaşdırılmış hesablama servisi.
 * Bu servis client-side preview üçündür, final hesablamalar backend-də aparılır.
 * 
 * @version 2.0.0
 * @author Herracim.az
 */

import axios from 'axios';

const API_BASE_URL = 'https://api.herracim.az';

// ═══════════════════════════════════════════════════════════════
// 📊 KONFİQURASİYA (Backend ilə eyni)
// ═══════════════════════════════════════════════════════════════

export const DEPOSIT_CONFIG = Object.freeze({
    MAX_DEPOSIT: 1000,
    PLATFORM_FEE_PERCENTAGE: 0.01,  // 1%
    MAX_FEE_FROM_DEPOSIT: 0.90,     // Depozitin max 90%-i
    MIN_SELLER_PERCENTAGE: 0.10,    // Satıcıya min 10%
});

// ═══════════════════════════════════════════════════════════════
// 📊 PİLLƏLİ DEPOZİT CƏDVƏLİ (Backend ilə eyni)
// ═══════════════════════════════════════════════════════════════

export const DEPOSIT_TIERS = Object.freeze([
    { min: 0, max: 100, deposit: 5 },
    { min: 100, max: 500, deposit: 10 },
    { min: 500, max: 1000, deposit: 20 },
    { min: 1000, max: 2500, deposit: 40 },
    { min: 2500, max: 5000, deposit: 75 },
    { min: 5000, max: 10000, deposit: 125 },
    { min: 10000, max: 25000, deposit: 250 },
    { min: 25000, max: 50000, deposit: 400 },
    { min: 50000, max: 100000, deposit: 650 },
    { min: 100000, max: Infinity, deposit: 1000 },
]);

// ═══════════════════════════════════════════════════════════════
// 📈 MİNİMUM TƏKLİF ARTIRIM CƏDVƏLİ (Backend ilə eyni)
// ═══════════════════════════════════════════════════════════════

export const BID_INCREMENT_TIERS = Object.freeze([
    { max: 500, increment: 5 },
    { max: 1000, increment: 10 },
    { max: 2500, increment: 20 },
    { max: 5000, increment: 50 },
    { max: 10000, increment: 70 },
    { max: 25000, increment: 130 },
    { max: 50000, increment: 210 },
    { max: 100000, increment: 380 },
    { max: 250000, increment: 750 },
    { max: Infinity, increment: 1000 },
]);

// ═══════════════════════════════════════════════════════════════
// 🔢 HESABLAMA FUNKSİYALARI
// ═══════════════════════════════════════════════════════════════

/**
 * Məhsulun başlanğıc qiymətinə görə depozit hesablayır
 * @param {number} productPrice - Məhsulun başlanğıc qiyməti
 * @returns {number} Depozit məbləği
 */
export const calculateDeposit = (productPrice) => {
    const price = Math.max(0, Number(productPrice) || 0);
    
    for (const tier of DEPOSIT_TIERS) {
        if (price >= tier.min && price < tier.max) {
            return tier.deposit;
        }
    }
    
    return DEPOSIT_CONFIG.MAX_DEPOSIT;
};

/**
 * Minimum təklif artırımını hesablayır
 * @param {number} currentPrice - Cari ən yüksək qiymət
 * @returns {number} Minimum artırım
 */
export const calculateMinBidIncrement = (currentPrice) => {
    const price = Math.max(0, Number(currentPrice) || 0);
    
    for (const tier of BID_INCREMENT_TIERS) {
        if (price < tier.max) {
            return tier.increment;
        }
    }
    
    return 1000;
};

/**
 * Növbəti minimum təklif məbləğini hesablayır
 * @param {number} currentPrice - Cari ən yüksək qiymət
 * @returns {number} Minimum təklif
 */
export const calculateNextMinBid = (currentPrice) => {
    const price = Number(currentPrice) || 0;
    return price + calculateMinBidIncrement(price);
};

/**
 * Platforma haqqını hesablayır
 * Formula: MIN(Ən yüksək təklifin 1%-i, Depozitin 90%-i)
 */
export const calculatePlatformFee = (highestBid, depositAmount) => {
    const percentageFee = highestBid * DEPOSIT_CONFIG.PLATFORM_FEE_PERCENTAGE;
    const maxFee = depositAmount * DEPOSIT_CONFIG.MAX_FEE_FROM_DEPOSIT;
    return Math.round(Math.min(percentageFee, maxFee) * 100) / 100;
};

/**
 * Satıcının alacağı məbləği hesablayır
 */
export const calculateSellerAmount = (depositAmount, platformFee) => {
    const sellerAmount = depositAmount - platformFee;
    const minAmount = depositAmount * DEPOSIT_CONFIG.MIN_SELLER_PERCENTAGE;
    return Math.round(Math.max(sellerAmount, minAmount) * 100) / 100;
};

/**
 * Tam hesablama - bütün məlumatları qaytarır
 */
export const calculateAll = (productPrice, currentHighestBid) => {
    const deposit = calculateDeposit(productPrice);
    const platformFee = calculatePlatformFee(currentHighestBid, deposit);
    const sellerGets = calculateSellerAmount(deposit, platformFee);
    const minIncrement = calculateMinBidIncrement(currentHighestBid);
    
    return {
        productPrice,
        currentHighestBid,
        depositAmount: deposit,
        platformFee,
        sellerGets,
        minBidIncrement: minIncrement,
        nextMinBid: currentHighestBid + minIncrement,
        maxDeposit: DEPOSIT_CONFIG.MAX_DEPOSIT,
        platformFeePercentage: DEPOSIT_CONFIG.PLATFORM_FEE_PERCENTAGE * 100,
    };
};

// ═══════════════════════════════════════════════════════════════
// ✅ VALİDASİYA FUNKSİYALARI
// ═══════════════════════════════════════════════════════════════

/**
 * Təklifin validliyini yoxlayır
 */
export const validateBid = (bidAmount, currentPrice) => {
    const bid = Number(bidAmount);
    const current = Number(currentPrice);
    
    if (isNaN(bid) || bid <= 0) {
        return {
            isValid: false,
            errorCode: 'INVALID_AMOUNT',
            message: 'Təklif məbləği düzgün deyil.'
        };
    }
    
    const minIncrement = calculateMinBidIncrement(current);
    const minRequired = current + minIncrement;
    
    if (bid < minRequired) {
        return {
            isValid: false,
            errorCode: 'BID_TOO_LOW',
            message: `Təklif ən azı ${formatCurrency(minRequired)} olmalıdır!`,
            minRequired,
            minIncrement
        };
    }
    
    return {
        isValid: true,
        errorCode: null,
        message: null,
        minRequired,
        minIncrement
    };
};

/**
 * Balansın kifayət edib-etmədiyini yoxlayır
 */
export const validateBalance = (balance, productPrice, hasExistingDeposit = false) => {
    if (hasExistingDeposit) {
        return {
            isValid: true,
            requiresDeposit: false,
            message: 'Artıq depozit ödəmisiniz.',
            requiredDeposit: 0
        };
    }
    
    const required = calculateDeposit(productPrice);
    const userBalance = Number(balance) || 0;
    
    if (userBalance < required) {
        return {
            isValid: false,
            requiresDeposit: true,
            message: `Balansda kifayət qədər məbləğ yoxdur!`,
            requiredDeposit: required,
            currentBalance: userBalance,
            shortfall: required - userBalance
        };
    }
    
    return {
        isValid: true,
        requiresDeposit: true,
        message: `Depozit tutulacaq: ${formatCurrency(required)}`,
        requiredDeposit: required,
        currentBalance: userBalance,
        remainingAfter: userBalance - required
    };
};

// ═══════════════════════════════════════════════════════════════
// 🎨 FORMAT FUNKSİYALARI
// ═══════════════════════════════════════════════════════════════

/**
 * Məbləği AZN formatında göstərir
 */
export const formatCurrency = (amount, showSymbol = true) => {
    const num = Number(amount);
    if (isNaN(num)) return showSymbol ? '0.00 AZN' : '0.00';
    
    const formatted = num.toLocaleString('az-AZ', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
    
    return showSymbol ? `${formatted} AZN` : formatted;
};

/**
 * Depozit cədvəlini oxunaqlı formatda qaytarır
 */
export const getFormattedDepositTable = () => {
    return DEPOSIT_TIERS.map(tier => ({
        range: tier.max === Infinity
            ? `${tier.min.toLocaleString('az-AZ')}+ AZN`
            : `${tier.min.toLocaleString('az-AZ')} - ${tier.max.toLocaleString('az-AZ')} AZN`,
        deposit: formatCurrency(tier.deposit),
        depositValue: tier.deposit,
        minPrice: tier.min,
        maxPrice: tier.max
    }));
};

// ═══════════════════════════════════════════════════════════════
// 🌐 API FUNKSİYALARI
// ═══════════════════════════════════════════════════════════════

/**
 * Backend-dən depozit məlumatlarını alır
 */
export const fetchDepositInfo = async (productId) => {
    try {
        const response = await axios.get(
            `${API_BASE_URL}/api/BidDeposit/GetDepositInfo/${productId}`
        );
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Depozit məlumatı alınmadı:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || 'Xəta baş verdi' 
        };
    }
};

/**
 * Backend-dən depozit cədvəlini alır
 */
export const fetchDepositTable = async () => {
    try {
        const response = await axios.get(
            `${API_BASE_URL}/api/BidDeposit/GetDepositTable`
        );
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Depozit cədvəli alınmadı:', error);
        return { success: false, error: 'Xəta baş verdi' };
    }
};

/**
 * İstifadəçinin depozit statusunu yoxlayır
 */
export const checkUserDeposit = async (productId) => {
    try {
        const response = await axios.get(
            `${API_BASE_URL}/api/BidDeposit/HasDeposit`,
            { 
                params: { productId },
                withCredentials: true 
            }
        );
        return { success: true, hasDeposit: response.data.hasDeposit };
    } catch (error) {
        return { success: false, hasDeposit: false };
    }
};

/**
 * İstifadəçi balansını alır
 */
export const fetchUserBalance = async (userId) => {
    try {
        const response = await axios.get(
            `${API_BASE_URL}/api/BidDeposit/GetBalance/${userId}`
        );
        return { 
            success: true, 
            data: {
                balance: response.data.balance,
                activeDeposits: response.data.activeDeposits,
                availableBalance: response.data.availableBalance,
                totalAssets: response.data.totalAssets
            }
        };
    } catch (error) {
        return { success: false, data: null };
    }
};

/**
 * Təklif göndərir (Pilləli depozit sistemi ilə)
 */
export const placeBid = async (userId, productId, bidAmount) => {
    try {
        const response = await axios.post(
            `${API_BASE_URL}/api/BidDeposit/PlaceBid`,
            null,
            { 
                params: { Id: userId, productId, bidAmount },
                withCredentials: true
            }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { 
            success: false, 
            error: error.response?.data?.message || 'Təklif göndərilə bilmədi',
            data: error.response?.data
        };
    }
};

/**
 * Depoziti təsdiqləyir (Qalib üçün)
 */
export const confirmDeposit = async (productId) => {
    try {
        const response = await axios.post(
            `${API_BASE_URL}/api/BidDeposit/ConfirmDeposit`,
            { productId },
            { 
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { 
            success: false, 
            error: error.response?.data?.message || 'Depozit təsdiqlənə bilmədi' 
        };
    }
};

// Default export
const DepositService = {
    // Config
    DEPOSIT_CONFIG,
    DEPOSIT_TIERS,
    BID_INCREMENT_TIERS,
    
    // Calculations
    calculateDeposit,
    calculateMinBidIncrement,
    calculateNextMinBid,
    calculatePlatformFee,
    calculateSellerAmount,
    calculateAll,
    
    // Validation
    validateBid,
    validateBalance,
    
    // Formatting
    formatCurrency,
    getFormattedDepositTable,
    
    // API
    fetchDepositInfo,
    fetchDepositTable,
    checkUserDeposit,
    fetchUserBalance,
    placeBid,
    confirmDeposit,
};

export default DepositService;